<template>
  <div class="analytics_filter_component">
    <ViewFilterComponent
      source="analytics_filter_component"
      :entire-data="true"
      :filters-options="filterOptions"
      :filter-data="filterData"
      @filter-changed="handleFilterChange"
      @clear-all-filters="clearAllFilters"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";
import {
  analyticsFilterEntity,
  multiSelectFilters,
} from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import ViewFilterComponent from "@/src/components/view_components/ViewFilterComponent.vue";
import { featureNames } from "@/src/constants/globalConstants.js";
import { channelTypeOptions } from "@/src/constants/analyticConstants.js";

const props = defineProps({
  allowedFilters: { type: Object, required: true },
});

const filterData = ref({});
const filterDataEntire = ref({});

const analyticsFilter = [
  {
    id: analyticsFilterEntity.USER,
    name: "User",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: analyticsFilterEntity.TEAM,
    name: "Team",
    options: [],
    searchable: true,
    multiSelect: false,
    showInitials: true,
  },
  {
    id: analyticsFilterEntity.PIPELINE,
    name: "Pipeline",
    options: [],
  },
  {
    id: analyticsFilterEntity.MAILBOX,
    name: "Mailbox",
    options: [],
    searchable: true,
  },
  {
    id: analyticsFilterEntity.MEETING_AGENT,
    name: "Meeting Agent",
    options: [],
    searchable: true,
    showInitials: true,
  },
  {
    id: analyticsFilterEntity.ROUTING_FORM,
    name: "Routing Form",
    options: [],
    searchable: true,
    showInitials: true,
  },
  {
    id: analyticsFilterEntity.SEQUENCE,
    name: featureNames.SEQUENCES,
    options: [],
    searchable: true,
  },
  {
    id: analyticsFilterEntity.CHANNEL_TYPE,
    name: "Channel Type",
    options: [],
    searchable: true,
  },
];

const filterOptions = computed(() => {
  return analyticsFilter.filter((filter) =>
    props.allowedFilters.includes(filter.id),
  );
});

const clearAllFilters = async () => {
  filterData.value = {
    user: [],
    team: [],
    pipeline: null,
    mailbox: null,
    sequence: null,
    meeting_agent: null,
    routing_form: null,
    channel_type: null,
  };

  filterDataEntire.value = {
    user: [],
    team: [],
    pipeline: null,
    mailbox: null,
    sequence: null,
    meeting_agent: null,
    routing_form: null,
    channel_type: null,
  };
  await handleFilterApply();
};

const handleFilterChange = ({ filter, updated_values }) => {
  try {
    if (updated_values) {
      if (multiSelectFilters.includes(filter)) {
        if (!filterDataEntire.value[filter]) {
          filterDataEntire.value[filter] = [];
        }
        if (!filterData.value[filter]) {
          filterData.value[filter] = [];
        }

        const existingIndex = filterDataEntire.value[filter].findIndex(
          (item) => item.id === updated_values.id,
        );

        if (existingIndex === -1) {
          filterDataEntire.value[filter] = [
            ...filterDataEntire.value[filter],
            updated_values,
          ];
          filterData.value[filter] = [
            ...new Set([...filterData.value[filter], updated_values.id]),
          ];
        } else {
          const newArray = [...filterDataEntire.value[filter]];
          newArray.splice(existingIndex, 1);
          filterDataEntire.value[filter] = newArray;
          filterData.value[filter] = newArray.map((item) => item.id);
        }
      } else {
        filterData.value[filter] = updated_values?.id;
        filterDataEntire.value[filter] = updated_values;
      }
    } else {
      if (filter in filterData.value) {
        if (multiSelectFilters.includes(filter)) {
          filterDataEntire.value[filter] = [];
          filterData.value[filter] = [];
        } else {
          delete filterData.value[filter];
          delete filterDataEntire.value[filter];
        }
      }
    }

    handleFilterApply();
  } catch (error) {
    console.error("An error occurred while handling filter change: ", error);
  }
};

const handleFilterApply = async () => {
  const filtersToStore = { ...filterDataEntire.value };

  multiSelectFilters.forEach((filter) => {
    if (filtersToStore[filter] && Array.isArray(filtersToStore[filter])) {
      const uniqueItems = [];
      const seenIds = new Set();

      filtersToStore[filter].forEach((item) => {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          uniqueItems.push(item);
        }
      });

      filtersToStore[filter] = uniqueItems;
      filterData.value[filter] = Array.from(seenIds);
    }
  });

  await store.dispatch("analytics/updateFilters", filtersToStore);
};

const setFilterOptions = (filter) => {
  switch (filter.id) {
    case analyticsFilterEntity.USER:
      filter.options = store.getters["analytics/getUsersList"] || [];
      break;
    case analyticsFilterEntity.TEAM:
      filter.options = store.getters["analytics/getTeamsList"] || [];
      break;
    case analyticsFilterEntity.PIPELINE:
      filter.options = store.getters["analytics/getPipelinesList"] || [];
      break;
    case analyticsFilterEntity.MAILBOX:
      filter.options = store.getters["analytics/getMailboxesList"] || [];
      break;
    case analyticsFilterEntity.SEQUENCE:
      filter.options = store.getters["analytics/getSequencesList"] || [];
      break;
    case analyticsFilterEntity.MEETING_AGENT:
      filter.options = store.getters["analytics/getMeetingAgentsList"] || [];
      break;
    case analyticsFilterEntity.ROUTING_FORM:
      filter.options = store.getters["analytics/getRoutingFormsList"] || [];
      break;
    case analyticsFilterEntity.CHANNEL_TYPE:
      filter.options = channelTypeOptions;
      break;
  }
};

const updateFilterOptions = () => {
  filterOptions.value.forEach(setFilterOptions);
};

onMounted(() => {
  const data = store.getters["analytics/getFilters"] || {};

  filterDataEntire.value = {
    user: Array.isArray(data.user)
      ? [...data.user]
      : data.user
        ? [data.user]
        : [],
    team: Array.isArray(data.team)
      ? [...data.team]
      : data.team
        ? [data.team]
        : [],
    pipeline: data.pipeline || null,
    mailbox: data.mailbox || null,
    sequence: data.sequence || null,
    meeting_agent: data.meeting_agent || null,
    routing_form: data.routing_form || null,
    channel_type: data.channel_type || null,
  };

  filterData.value = {
    user: filterDataEntire.value.user.map((item) => item.id),
    team: filterDataEntire.value.team.map((item) => item.id),
    pipeline: filterDataEntire.value.pipeline?.id || null,
    mailbox: filterDataEntire.value.mailbox?.id || null,
    sequence: filterDataEntire.value.sequence?.id || null,
    meeting_agent: filterDataEntire.value.meeting_agent?.id || null,
    routing_form: filterDataEntire.value.routing_form?.id || null,
    channel_type: filterDataEntire.value.channel_type?.id || null,
  };

  analyticsFilter.forEach((filter) => {
    if (props.allowedFilters.includes(filter.id)) {
      setFilterOptions(filter);
    }
  });

  handleFilterApply();
});

watch(
  () => props.allowedFilters,
  () => {
    updateFilterOptions();
  },
  { deep: true },
);
</script>
